import React, { useEffect, useMemo, useState } from "react";
import { useRoute, useNavigation, useIsFocused } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  useGetCompanionChatByAiTutorIdQuery,
  useGetAiTutorVideosQuery,
  useGetAiTutorQuery,
  useGetCourseChatResourcesQuery,
} from "../graphql/generated/graphql";
import { useAppState } from "../contexts/AppStateContext";
import { sortResourceItemsOfModules } from "../utils/sortResourceItemsOfModules";
import AITutorChatLayoutContainer from "../containers/chat/AITutorChatLayoutContainer/AITutorChatLayoutContainer";
import AITutorChatLayoutContainerV2 from "../containers/chat/AITutorChatLayoutContainerV2/AITutorChatLayoutContainerV2";
import { useAppConfig } from "../AppConfigProvider";
import type { Objective } from "../containers/companion/LearningGoalsContainer";
import type { ExplorationMediaItem } from "../components/learning-activity/ExplorationMenu/ExplorationMenu";

const isObjectiveCompleted = (objective: { status?: string | null; isCompleted?: boolean | null }): boolean => {
  if (typeof objective.isCompleted === "boolean") {
    return objective.isCompleted;
  }
  return objective.status?.trim().toLowerCase() === "completed";
};

const CustomCourseAITutorChatView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course AITutor">>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const isFocused = useIsFocused();
  const { resource_id, course_id, integration_id, module_id } = route.params;
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const { state: appState } = useAppState();
  const { useILEGraphV2 } = useAppConfig() ?? {};

  const {
    data: aiTutorData,
    loading: aiTutorLoading,
    refetch: refetchAiTutor,
  } = useGetAiTutorQuery({
    variables: { id: resource_id },
    skip: !resource_id,
  });

  const { data: companionChatByAiTutorData, loading: companionChatByAiTutorLoading } =
    useGetCompanionChatByAiTutorIdQuery({
      variables: { aiTutorId: resource_id },
      skip: !resource_id,
      fetchPolicy: "network-only",
    });

  const { data: aiTutorVideosData, refetch: refetchAiTutorVideos } = useGetAiTutorVideosQuery({
    variables: { id: resource_id },
    skip: !resource_id,
    fetchPolicy: "network-only",
  });

  const { data: courseData } = useGetCourseChatResourcesQuery({
    variables: { id: course_id },
    skip: !course_id,
  });

  const formattedModules = useMemo(() => {
    const modules = courseData?.getCourseChatResources?.data?.[0]?.modules;
    if (!modules) return undefined;
    return modules.map((module) => {
      const aiTutors = module.aiTutors.map((aiTutor) => ({ ...aiTutor, resourceType: "aiTutor" }));
      const axioResources = module.axioResources.map((resource) => ({ ...resource, resourceType: "axioResource" }));
      const assignments = module.assignments.map((assignment) => ({ ...assignment, resourceType: "assignment" }));
      const assessments = module.assessment ? [{ ...module.assessment, resourceType: "assessment" }] : [];
      const projects = module.projects.map((project) => ({ ...project, resourceType: "project" }));
      const allResources = [...aiTutors, ...axioResources, ...assignments, ...assessments, ...projects];
      return {
        moduleId: module.id,
        resources: sortResourceItemsOfModules(allResources, module.resourceOrder),
      };
    });
  }, [courseData]);

  const currentModuleIndex = formattedModules?.findIndex((m) => m.moduleId === module_id);
  const currentModule = formattedModules?.[currentModuleIndex ?? -1];
  const currentResourceIndex = currentModule?.resources.findIndex((r) => r.id === resource_id);

  const nextActivity = useMemo(() => {
    if (
      !formattedModules ||
      currentModuleIndex === null ||
      currentModuleIndex < 0 ||
      !currentModule ||
      currentResourceIndex === null ||
      currentResourceIndex < 0
    ) {
      return null;
    }
    if (currentResourceIndex < currentModule.resources.length - 1) {
      return currentModule.resources[currentResourceIndex + 1];
    }
    const nextModule = formattedModules[currentModuleIndex + 1];
    if (nextModule && nextModule.resources.length > 0) {
      return nextModule.resources[0];
    }
    return null;
  }, [formattedModules, currentModuleIndex, currentModule, currentResourceIndex]);

  const handleExitPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      const moduleId = route.params?.module_id;
      if (moduleId && typeof moduleId === "string") {
        const integrationId =
          typeof route.params?.integration_id === "string" ? route.params.integration_id : course_id;
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: course_id,
          integration_id: integrationId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id: course_id,
          integration_id,
        });
      }
    }
  };

  const handleNextActivityPress = () => {
    if (!nextActivity) return;
    const targetModuleId = formattedModules?.find((m) => m.resources.some((r) => r.id === nextActivity.id))?.moduleId;

    switch (nextActivity.resourceType) {
      case "aiTutor":
        navigation.navigate("Custom Course AITutor", {
          resource_type: "aiTutor",
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;
      case "assignment":
        navigation.navigate("Custom Assignment Student", {
          resource_type: "assignment",
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;
      case "project":
        navigation.navigate("Project", {
          id: nextActivity.id,
          courseId: course_id,
          integrationId: integration_id,
        });
        break;
      case "axioResource":
        navigation.navigate("Custom Resource Student", {
          resource_type: nextActivity.resourceType,
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;
      case "assessment":
        navigation.navigate("Custom Course Assessment", {
          assessment_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;
      default:
        console.warn("⚠️ Unknown next activity type", nextActivity);
    }
  };

  const explorationItems = useMemo<ExplorationMediaItem[]>(() => {
    const videoItems: ExplorationMediaItem[] =
      (aiTutorVideosData?.getAITutorVideos?.data ?? []).map((video) => ({
        id: video.id,
        title: video.title,
        type: "video",
        metadata: "Suggested",
        thumbnail:
          video.thumbnailUrl || (video.videoId ? `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg` : ""),
        videoId: video.videoId,
        description: video.description ?? undefined,
        mediaUrl: video.url ?? undefined,
      })) ?? [];

    const aiTutor = aiTutorData?.getAITutor?.data?.[0];
    let imageItems: ExplorationMediaItem[] = [];
    if (aiTutor) {
      const objectives = aiTutor.objectives ?? [];
      const firstIncomplete = objectives.find((o) => (o?.status ?? "").trim().toLowerCase() !== "completed");
      const activeObjective = firstIncomplete ?? objectives[objectives.length - 1];
      const activeImages = activeObjective?.imagePool ?? [];

      imageItems = activeImages.map((img) => ({
        id: img.id,
        title: img.objectiveText ?? "Illustration",
        type: "image" as const,
        metadata: "Suggested",
        thumbnail: img.url,
        description: img.objectiveText ?? undefined,
        mediaUrl: img.url,
      }));
    }

    return [...videoItems, ...imageItems];
  }, [aiTutorVideosData, aiTutorData]);

  const objectiveData = useMemo<Objective[]>(() => {
    const objectives = aiTutorData?.getAITutor?.data?.[0]?.objectives ?? [];
    return objectives.reduce<Objective[]>((acc, objective) => {
      if (!objective) return acc;
      acc.push({
        ...objective,
        isCompleted: isObjectiveCompleted(objective),
      });
      return acc;
    }, []);
  }, [aiTutorData]);

  const handleObjectiveCompleted = () => {
    if (!resource_id) return;
    void Promise.all([refetchAiTutor({ id: resource_id }), refetchAiTutorVideos({ id: resource_id })]);
  };

  useEffect(() => {
    if (appState.meta.isAtDayLimit || appState.meta.isAtLifetimeLimit) {
      setUpgradeModalVisible(true);
    }
  }, [appState.meta.isAtDayLimit, appState.meta.isAtLifetimeLimit]);

  useEffect(() => {
    if (!resource_id) return;
    refetchAiTutorVideos();
  }, [objectiveData, refetchAiTutorVideos, resource_id]);

  if (useILEGraphV2) {
    return (
      <AITutorChatLayoutContainerV2
        resourceId={resource_id}
        title={aiTutorData?.getAITutor?.data?.[0]?.title ?? ""}
        loading={aiTutorLoading}
        backButtonText="Back to course"
        onBackPress={handleExitPress}
        onNextActivityPress={handleNextActivityPress}
        hasNextActivity={Boolean(nextActivity)}
        showUpgradeModal={upgradeModalVisible}
        onCloseUpgradeModal={() => setUpgradeModalVisible(false)}
        chatData={companionChatByAiTutorData?.getCompanionChatByAiTutorId?.data ?? undefined}
        chatLoading={companionChatByAiTutorLoading}
        objectives={objectiveData}
        onObjectiveCompleted={handleObjectiveCompleted}
        isScreenFocused={isFocused}
        explorationItems={explorationItems}
      />
    );
  }
  return (
    <AITutorChatLayoutContainer
      resourceId={resource_id}
      title={aiTutorData?.getAITutor?.data?.[0]?.title ?? ""}
      loading={aiTutorLoading}
      backButtonText="Back to course"
      onBackPress={handleExitPress}
      onNextActivityPress={handleNextActivityPress}
      hasNextActivity={Boolean(nextActivity)}
      showUpgradeModal={upgradeModalVisible}
      onCloseUpgradeModal={() => setUpgradeModalVisible(false)}
      chatData={companionChatByAiTutorData?.getCompanionChatByAiTutorId?.data}
      chatLoading={companionChatByAiTutorLoading}
      objectives={objectiveData}
      onObjectiveCompleted={handleObjectiveCompleted}
      isScreenFocused={isFocused}
      explorationItems={explorationItems}
    />
  );
};

export default CustomCourseAITutorChatView;
